import {
  LIMIT_FIELDS,
  LimitField,
  LimitMetricSpec,
  LimitDomains,
  LimitResult,
} from './types';
import { parseNumericInterval } from './interval';
import { normalizeCelsiusUnit, resolveLimitDomains } from './domains';

export interface ThermostatLimitDomainDevice {
  source?: { type?: string; model?: string } | null;
  deviceId?: string;
  meta?: {
    manufacturer?: string;
    type?: string;
    metrics?:
      | ReadonlyMap<string, LimitMetricSpec>
      | Readonly<Record<string, LimitMetricSpec>>;
  } | null;
  deviceMetrics?: ReadonlyArray<{
    key?: string | null;
    value?: LimitMetricSpec | null;
  } | null> | null;
  deviceSpec?: {
    thermostat?: { controlContract?: string; supportedAutoModeType?: string };
  } | null;
}
export function thermostatMetricSpecs(
  device: ThermostatLimitDomainDevice,
): Readonly<Record<string, LimitMetricSpec>> {
  const source = device.meta?.metrics;
  const entries: [string, LimitMetricSpec][] =
    source instanceof Map
      ? [...source]
      : source
        ? Object.entries(source)
        : (device.deviceMetrics ?? [])
            .filter((entry) => entry?.key && entry.value)
            .map((entry) => [entry!.key!, entry!.value!]);
  return Object.fromEntries(
    entries.map(([key, spec]) => {
      const hydrated = spec as LimitMetricSpec & {
        toObject?: (options: object) => LimitMetricSpec;
      };
      return [
        key,
        typeof hydrated?.toObject === 'function'
          ? hydrated.toObject({ virtuals: false, flattenMaps: true })
          : spec,
      ];
    }),
  );
}
export function isLegacyCoreThermostat(
  device: ThermostatLimitDomainDevice,
): boolean {
  if (
    device.meta?.manufacturer?.toLowerCase().includes('mitsubishi') ||
    device.meta?.type?.startsWith('mitsubishi') ||
    device.deviceSpec?.thermostat?.controlContract === 'separate-power-v1'
  )
    return false;
  if (device.source?.type) return device.source.type === 'Core';
  return /^ZigBee/i.test(device.deviceId ?? '');
}
/** Old Core publications used [0,100] as a placeholder. Normalize only while reading. */
export function effectiveThermostatLimitSpecs(
  device: ThermostatLimitDomainDevice,
): Readonly<Partial<Record<LimitField, LimitMetricSpec>>> {
  const metrics = { ...thermostatMetricSpecs(device) },
    legacy = isLegacyCoreThermostat(device);
  const result: Partial<Record<LimitField, LimitMetricSpec>> = {};
  if (
    !LIMIT_FIELDS.some((field) => metrics[field] !== undefined) &&
    (legacy || device.source?.type === 'HMM')
  ) {
    for (const field of LIMIT_FIELDS)
      metrics[field] = {
        value: legacy
          ? '[0,100]'
          : field.includes('Heat')
            ? '[7,30]'
            : '[16,32]',
        units: 'C',
        valueType: 'number',
        readOnly: false,
      };
  }
  for (const field of LIMIT_FIELDS) {
    const spec = metrics[field];
    if (!spec) continue;
    const parsed = parseNumericInterval(spec.value);
    if (
      !legacy ||
      !parsed.ok ||
      parsed.value.lower !== 0 ||
      parsed.value.upper !== 100 ||
      !parsed.value.lowerInclusive ||
      !parsed.value.upperInclusive
    ) {
      result[field] = spec;
      continue;
    }
    const heat = field.includes('Heat');
    let lower = heat ? 7 : 16,
      upper = heat ? 30 : 32;
    let lowerInclusive = true,
      upperInclusive = true;
    const target = metrics[heat ? 'heatSetpoint' : 'coolSetpoint'];
    const native = parseNumericInterval(target?.value);
    // A malformed descriptor must not widen the established Core domain.
    if (native.ok && normalizeCelsiusUnit(target?.units ?? target?.unit)) {
      if (native.value.lower >= lower) {
        lower = native.value.lower;
        lowerInclusive = native.value.lowerInclusive;
      }
      if (native.value.upper <= upper) {
        upper = native.value.upper;
        upperInclusive = native.value.upperInclusive;
      }
    }
    result[field] = {
      ...spec,
      value: `${lowerInclusive ? '[' : '('}${lower},${upper}${upperInclusive ? ']' : ')'}`,
    };
  }
  return result;
}
export function resolveEffectiveThermostatLimitDomains(
  device: ThermostatLimitDomainDevice,
): LimitResult<LimitDomains> {
  return resolveLimitDomains(effectiveThermostatLimitSpecs(device));
}
